import { css } from '@emotion/react';
import { tokens, theme as baseTheme } from './theme';

// Enhanced theme system that builds on existing tokens
export const createEmotionTheme = (themeId = 'dark') => {
  const themeTokens = baseTheme[themeId];
  const baseTokens = tokens.base;

  return {
    // Keep existing token structure
    ...baseTokens,
    ...themeTokens,

    // Add enhanced theming utilities
    colors: {
      background: `rgb(${themeTokens.rgbBackground})`,
      backgroundLight: `rgb(${themeTokens.rgbBackgroundLight})`,
      primary: `rgb(${themeTokens.rgbPrimary})`,
      accent: `rgb(${themeTokens.rgbAccent})`,
      text: `rgb(${themeTokens.rgbText})`,
      error: `rgb(${themeTokens.rgbError})`,
      textTitle: themeTokens.colorTextTitle,
      textBody: themeTokens.colorTextBody,
      textLight: themeTokens.colorTextLight,
    },

    // Spacing utilities
    spacing: {
      xs: baseTokens.spaceXS,
      s: baseTokens.spaceS,
      m: baseTokens.spaceM,
      l: baseTokens.spaceL,
      xl: baseTokens.spaceXL,
      '2xl': baseTokens.space2XL,
      '3xl': baseTokens.space3XL,
      '4xl': baseTokens.space4XL,
      '5xl': baseTokens.space5XL,
      outer: baseTokens.spaceOuter,
    },

    // Typography utilities
    typography: {
      fontStack: baseTokens.fontStack,
      systemFontStack: baseTokens.systemFontStack,
      monoFontStack: baseTokens.monoFontStack,
      weights: {
        regular: baseTokens.fontWeightRegular,
        medium: baseTokens.fontWeightMedium,
        bold: baseTokens.fontWeightBold,
      },
      sizes: {
        h0: baseTokens.fontSizeH0,
        h1: baseTokens.fontSizeH1,
        h2: baseTokens.fontSizeH2,
        h3: baseTokens.fontSizeH3,
        h4: baseTokens.fontSizeH4,
        bodyXL: baseTokens.fontSizeBodyXL,
        bodyL: baseTokens.fontSizeBodyL,
        bodyM: baseTokens.fontSizeBodyM,
        bodyS: baseTokens.fontSizeBodyS,
        bodyXS: baseTokens.fontSizeBodyXS,
      },
    },

    // Animation utilities
    animation: {
      duration: {
        xs: baseTokens.durationXS,
        s: baseTokens.durationS,
        m: baseTokens.durationM,
        l: baseTokens.durationL,
        xl: baseTokens.durationXL,
      },
      easing: {
        fastOutSlowIn: baseTokens.bezierFastoutSlowin,
      },
    },

    // Breakpoints for responsive design
    breakpoints: {
      mobile: '768px',
      tablet: '1024px',
      laptop: '1200px',
      desktop: '1440px',
    },

    // Media queries
    media: {
      mobile: '@media (max-width: 768px)',
      tablet: '@media (max-width: 1024px)',
      laptop: '@media (max-width: 1200px)',
      desktop: '@media (min-width: 1440px)',
      reduceMotion: '@media (prefers-reduced-motion: reduce)',
    },
  };
};

// Common styled-component mixins
export const mixins = {
  // Responsive text styles
  headingStyle: (level = 'h1', theme) => css`
    font-family: ${theme.typography.fontStack};
    font-weight: ${theme.typography.weights.medium};
    font-size: ${theme.typography.sizes[level]};
    line-height: ${theme.lineHeightTitle};
    color: ${theme.colors.textTitle};
  `,

  // Body text styles
  bodyStyle: (size = 'bodyM', theme) => css`
    font-family: ${theme.typography.fontStack};
    font-weight: ${theme.typography.weights.regular};
    font-size: ${theme.typography.sizes[size]};
    line-height: ${theme.lineHeightBody};
    color: ${theme.colors.textBody};
  `,

  // Focus styles
  focusStyle: theme => css`
    &:focus {
      outline: none;
      box-shadow: 0 0 0 4px ${theme.colors.background}, 0 0 0 8px ${theme.colors.text};
    }

    &:active {
      box-shadow: none;
    }
  `,

  // Smooth transitions
  transition: (property = 'all', theme) => css`
    transition: ${property} ${theme.animation.duration.m}
      ${theme.animation.easing.fastOutSlowIn};
  `,

  // Center content
  centerContent: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,

  // Visually hidden (for accessibility)
  visuallyHidden: css`
    position: absolute !important;
    clip: rect(1px, 1px, 1px, 1px) !important;
    overflow: hidden !important;
    height: 1px !important;
    width: 1px !important;
    padding: 0 !important;
    border: 0 !important;
  `,
};

// Theme-aware helper functions
export const getThemeValue = (path, theme) => {
  return path.split('.').reduce((obj, key) => obj?.[key], theme);
};

export const rgba = (color, alpha) => {
  // Convert rgb(r, g, b) to rgba(r, g, b, alpha)
  return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
};

export default createEmotionTheme;
